import { apiFetch, toArray } from '@/services/core/apiFetch'
import { getContext } from '@/services/core/context'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

/** A currency the platform can convert to/from for the current network. */
export interface AvailableCurrency {
  /** ISO 4217 code, e.g. `NOK`. */
  currency: string
  /** Minor-unit digits, e.g. 2 for NOK, 0 for JPY. */
  defaultFractionDigits: number
}

/**
 * Display names for the currencies the ad server supports. The API only
 * returns codes, so names live here; unknown codes fall back to the code.
 */
export const CURRENCY_NAMES: Record<string, string> = {
  AED: 'United Arab Emirates Dirham',
  AUD: 'Australian Dollar',
  BGN: 'Bulgarian Lev',
  BRL: 'Brazilian Real',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  EUR: 'Euro',
  GBP: 'British Pound Sterling',
  HKD: 'Hong Kong Dollar',
  HRK: 'Croatian Kuna',
  HUF: 'Hungarian Forint',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli New Shekel',
  INR: 'Indian Rupee',
  ISK: 'Icelandic Krona',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  MXN: 'Mexican Peso',
  MYR: 'Malaysian Ringgit',
  NOK: 'Norwegian Krone',
  NZD: 'New Zealand Dollar',
  PHP: 'Philippine Peso',
  PLN: 'Polish Zloty',
  RON: 'Romanian Leu',
  RUB: 'Russian Ruble',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  TRY: 'Turkish Lira',
  USD: 'US Dollar',
  ZAR: 'South African Rand',
}

/** `NOK – Norwegian Krone`, or just the code when the name is unknown. */
export function currencyLabel(code: string): string {
  const name = CURRENCY_NAMES[code]
  return name ? `${code} – ${name}` : code
}

export const CurrenciesApi = {
  /**
   * Currencies available to the network, i.e. those the network's exchange
   * rate provider has rates for today. Mirrors `/v1/availablecurrencies`.
   */
  getAvailable: async (): Promise<AvailableCurrency[]> => {
    const res = await apiFetch(`${API_BASE}/availablecurrencies?context=${getContext()}`, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error(`Failed to fetch available currencies (${res.status})`)
    const data = await res.json()
    return toArray<AvailableCurrency>(data).filter((c) => !!c.currency)
  },
}
